import type React from "react";
import { useEffect, useMemo, useState } from "react";
import type { ReactElement } from "react";
import {
  Package,
  Users,
  Calendar,
  ArrowRight,
  ArrowUpRight,
  ArrowDownRight,
  Truck,
  CheckCircle2,
  ListChecks,
  TrendingUp,
  Globe,
  MapPin,
  Building2,
  Plus,
} from "lucide-react";
import {
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Sector,
  Line,
  LineChart,
  BarChart,
  Bar,
} from "recharts";
import { useAuth } from "../../hooks/useAuth";
import { useModules } from "../../hooks/useModules";
import { Link, useNavigate } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "../../hooks/useStore";
import {
  clearError,
  getDashboardSummary,
} from "../../store/slices/dashboardSlice";
import { toast } from "react-toastify";
import { STATUS_STEPS } from "../../constants/formOptions";
import { useTranslation } from "react-i18next";
import type { DashboardTrendDelta } from "../../types";

// ── Status palette (kept aligned with the rest of the app) ───────────────────
const STATUS_COLORS = {
  Completed: "#22c55e", // green-500
  InTransit: "#3b82f6", // blue-500
  AwaitingPickup: "#f97316", // orange-500
  ReadyForPickup: "#a855f7", // purple-500
  Order: "#6b7280", // gray-500
} as const;

// ── Skeleton ─────────────────────────────────────────────────────────────────
const Skeleton = ({ type }: { type: "kpi" | "chart" | "table" | "members" }) => {
  if (type === "kpi") {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
        <div className="flex items-start justify-between mb-4">
          <div className="h-10 w-10 bg-gray-200 rounded-lg animate-pulse" />
          <div className="h-4 w-12 bg-gray-200 rounded animate-pulse" />
        </div>
        <div className="h-3 w-24 bg-gray-200 rounded animate-pulse mb-2" />
        <div className="h-7 w-16 bg-gray-200 rounded animate-pulse mb-3" />
        <div className="h-8 w-full bg-gray-100 rounded animate-pulse" />
      </div>
    );
  }
  if (type === "chart") {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="h-6 w-48 bg-gray-200 rounded animate-pulse mb-5" />
        <div className="h-64 w-full bg-gray-100 rounded animate-pulse" />
      </div>
    );
  }
  if (type === "table") {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="h-6 w-40 bg-gray-200 rounded animate-pulse mb-5" />
        <div className="space-y-2">
          {Array.from({ length: 5 }, (_, i) => (
            <div key={i} className="h-10 bg-gray-50 rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="h-6 w-48 bg-gray-200 rounded animate-pulse mb-5" />
      <div className="space-y-3">
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gray-200 rounded-full animate-pulse" />
            <div className="flex-1 space-y-2">
              <div className="h-3 w-32 bg-gray-200 rounded animate-pulse" />
              <div className="h-2 w-48 bg-gray-100 rounded animate-pulse" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── KPI tile with sparkline ──────────────────────────────────────────────────
interface KpiTileProps {
  label: string;
  value: number;
  icon: React.ReactNode;
  accent: "primary" | "blue" | "green" | "purple" | "orange";
  trend?: DashboardTrendDelta;
  trendLabel: string;
  sparkData?: { date: string; value: number }[];
  sparkColor: string;
}

const KpiTile: React.FC<KpiTileProps> = ({
  label,
  value,
  icon,
  accent,
  trend,
  trendLabel,
  sparkData,
  sparkColor,
}) => {
  const accentMap: Record<KpiTileProps["accent"], string> = {
    primary: "bg-primary-50 text-primary-600",
    blue: "bg-blue-50 text-blue-600",
    green: "bg-green-50 text-green-600",
    purple: "bg-purple-50 text-purple-600",
    orange: "bg-orange-50 text-orange-600",
  };

  const showTrend = trend && (trend.value !== 0 || trend.percent !== 0);
  const trendUp = trend && trend.value >= 0;

  return (
    <div className="group bg-white rounded-xl shadow-sm border border-gray-200 p-5 transition hover:shadow-md hover:-translate-y-0.5">
      <div className="flex items-start justify-between mb-3">
        <div
          className={`h-10 w-10 rounded-lg flex items-center justify-center ${accentMap[accent]}`}
        >
          {icon}
        </div>
        {showTrend ? (
          <span
            className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${
              trendUp ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"
            }`}
            title={trendLabel}
          >
            {trendUp ? (
              <ArrowUpRight className="w-3 h-3" />
            ) : (
              <ArrowDownRight className="w-3 h-3" />
            )}
            {Math.abs(trend!.percent)}%
          </span>
        ) : null}
      </div>
      <p className="text-xs font-medium text-dark-500 uppercase tracking-wide">
        {label}
      </p>
      <p className="text-2xl font-bold text-dark-900 mt-1">{value}</p>
      {sparkData && sparkData.length > 1 && (
        <div className="h-10 mt-3 -mx-1">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={sparkData}>
              <Line
                type="monotone"
                dataKey="value"
                stroke={sparkColor}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

// ── Custom active shape for the donut center ─────────────────────────────────
interface DonutShapeProps {
  cx?: number;
  cy?: number;
  innerRadius?: number;
  outerRadius?: number;
  startAngle?: number;
  endAngle?: number;
  fill?: string;
}

const DonutActiveShape = (props: DonutShapeProps) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;
  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={(outerRadius ?? 0) + 4}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
    </g>
  );
};

// ── Tooltip styling for recharts ─────────────────────────────────────────────
const tooltipContentStyle = {
  backgroundColor: "rgba(17, 24, 39, 0.95)",
  border: "none",
  borderRadius: "8px",
  padding: "8px 12px",
  color: "white",
  fontSize: "12px",
  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
};
const tooltipLabelStyle = { color: "#9ca3af", marginBottom: 4 };
const tooltipItemStyle = { color: "white" };

const DashboardPage: React.FC = (): ReactElement => {
  const { t } = useTranslation("dashboard");
  const { user } = useAuth();
  const { has } = useModules();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // The recent-shipments panel is data the summary endpoint returns anyway, so
  // it stays. What has to be conditional is anything that NAVIGATES into the
  // shipments module: a link or a clickable row is an offer, and offering a
  // page the router will bounce them off is worse than not offering it.
  const canOpenShipments = has("shipments");

  const {
    kpis,
    trends,
    statusCounts,
    recentShipments,
    recentMembers,
    dailyTimeseries,
    period,
    loading,
    error,
  } = useAppSelector((state) => state.dashboard);

  const isAdmin = user?.role === "admin";

  const calculateDaysAgo = (createdAt: string) =>
    Math.floor(
      (Date.now() - new Date(createdAt).getTime()) / (1000 * 60 * 60 * 24),
    );

  const getStatusBadgeColor = (status: string) => {
    const colors: Record<string, string> = {
      Order: "bg-gray-100 text-gray-800",
      ReadyForPickup: "bg-purple-100 text-purple-700",
      AwaitingPickup: "bg-orange-100 text-orange-800",
      InTransit: "bg-blue-100 text-blue-800",
      Completed: "bg-green-100 text-green-800",
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  const { greeting, formattedDate } = useMemo(() => {
    const now = new Date();
    const hour = now.getHours();

    const greetingKey =
      hour >= 5 && hour < 12
        ? "morning"
        : hour >= 12 && hour < 17
          ? "day"
          : hour >= 17 && hour < 24
            ? "evening"
            : "night";

    const weekdayKeys = [
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
    ] as const;

    const weekdayLabel = t(`date.weekdays.${weekdayKeys[now.getDay()]}`);
    const formatted = `${weekdayLabel}, ${now
      .getDate()
      .toString()
      .padStart(2, "0")}.${(now.getMonth() + 1)
      .toString()
      .padStart(2, "0")}.${now.getFullYear().toString().slice(-2)}`;

    return { greeting: t(`greeting.${greetingKey}`), formattedDate: formatted };
  }, [t]);

  // Donut data
  const donutData = useMemo(
    () => [
      {
        key: "Completed",
        name: t("statusLabels.completed"),
        value: statusCounts.Completed,
        color: STATUS_COLORS.Completed,
      },
      {
        key: "InTransit",
        name: t("statusLabels.inTransit"),
        value: statusCounts.InTransit,
        color: STATUS_COLORS.InTransit,
      },
      {
        key: "AwaitingPickup",
        name: t("statusLabels.pickedUp"),
        value: statusCounts.AwaitingPickup,
        color: STATUS_COLORS.AwaitingPickup,
      },
      {
        key: "ReadyForPickup",
        name: t("statusLabels.readyForPickup"),
        value: statusCounts.ReadyForPickup,
        color: STATUS_COLORS.ReadyForPickup,
      },
      {
        key: "Order",
        name: t("statusLabels.order"),
        value: statusCounts.Order,
        color: STATUS_COLORS.Order,
      },
    ],
    [t, statusCounts],
  );

  const totalForChart = donutData.reduce((s, x) => s + x.value, 0);

  // Smart aggregation: daily / weekly / monthly buckets based on period length.
  // Keeps bars meaningful when the date range is long (avoids lonely spike pixels).
  const chartData = useMemo(() => {
    if (dailyTimeseries.length === 0) return [];

    const days = period.days || dailyTimeseries.length;
    const granularity: "day" | "week" | "month" =
      days <= 14 ? "day" : days <= 90 ? "week" : "month";

    const bucketKey = (date: Date): string => {
      if (granularity === "day") return date.toISOString().slice(0, 10);
      if (granularity === "week") {
        // ISO-ish: anchor to start of week (Mon)
        const d = new Date(date);
        const dow = (d.getDay() + 6) % 7;
        d.setDate(d.getDate() - dow);
        return d.toISOString().slice(0, 10);
      }
      // month
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-01`;
    };

    const bucketLabel = (date: Date): string => {
      if (granularity === "day") {
        return date.toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
        });
      }
      if (granularity === "week") {
        return date.toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
        });
      }
      return date.toLocaleDateString(undefined, {
        month: "short",
        year: "2-digit",
      });
    };

    const buckets = new Map<
      string,
      {
        key: string;
        label: string;
        date: string;
        total: number;
        completed: number;
        inTransit: number;
        awaitingPickup: number;
        readyForPickup: number;
        order: number;
      }
    >();

    for (const d of dailyTimeseries) {
      const date = new Date(d.date);
      const key = bucketKey(date);
      const existing = buckets.get(key);
      if (existing) {
        existing.total += d.total;
        existing.completed += d.completed;
        existing.inTransit += d.inTransit;
        existing.awaitingPickup += d.awaitingPickup;
        existing.readyForPickup += d.readyForPickup;
        existing.order += d.order;
      } else {
        buckets.set(key, {
          key,
          label: bucketLabel(new Date(key)),
          date: key,
          total: d.total,
          completed: d.completed,
          inTransit: d.inTransit,
          awaitingPickup: d.awaitingPickup,
          readyForPickup: d.readyForPickup,
          order: d.order,
        });
      }
    }

    return Array.from(buckets.values()).sort((a, b) =>
      a.date.localeCompare(b.date),
    );
  }, [dailyTimeseries, period.days]);

  // Sparkline per KPI
  const totalSparkline = useMemo(
    () => dailyTimeseries.map((d) => ({ date: d.date, value: d.total })),
    [dailyTimeseries],
  );
  const inTransitSparkline = useMemo(
    () => dailyTimeseries.map((d) => ({ date: d.date, value: d.inTransit })),
    [dailyTimeseries],
  );
  const completedSparkline = useMemo(
    () => dailyTimeseries.map((d) => ({ date: d.date, value: d.completed })),
    [dailyTimeseries],
  );

  useEffect(() => {
    dispatch(getDashboardSummary({ days: period.days || 30 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handlePeriodChange = (days: number) => {
    dispatch(getDashboardSummary({ days }));
  };

  const trendLabel = t("kpis.vsPrevious");

  const initialLoading = loading && dailyTimeseries.length === 0;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-dark-900 mb-1">
            {greeting}, {user?.firstName}!
          </h1>
          <p className="text-base font-normal text-dark-600">
            {t("date.todayIs")} {formattedDate}
          </p>
        </div>
        <select
          value={period.days || 30}
          onChange={(e) => handlePeriodChange(Number(e.target.value))}
          className="text-sm bg-white rounded-lg px-3 py-2 outline-none cursor-pointer text-dark-700 hover:bg-gray-50 border border-gray-200 shadow-sm self-start md:self-auto"
          aria-label="Time period"
        >
          {([7, 30, 90, 365] as const).map((d) => (
            <option key={d} value={d}>
              {t(`shipmentOverview.timePeriod.${d}`)}
            </option>
          ))}
        </select>
      </div>

      {/* KPI tiles */}
      {initialLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} type="kpi" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiTile
            label={t("kpis.totalShipments")}
            value={kpis.totalShipments}
            icon={<Package className="w-5 h-5" />}
            accent="primary"
            trend={trends.totalShipments}
            trendLabel={trendLabel}
            sparkData={totalSparkline}
            sparkColor="#2563eb"
          />
          <KpiTile
            label={t("kpis.inTransit")}
            value={kpis.inTransit}
            icon={<Truck className="w-5 h-5" />}
            accent="blue"
            trend={trends.inTransit}
            trendLabel={trendLabel}
            sparkData={inTransitSparkline}
            sparkColor={STATUS_COLORS.InTransit}
          />
          <KpiTile
            label={t("kpis.completed")}
            value={kpis.completed}
            icon={<CheckCircle2 className="w-5 h-5" />}
            accent="green"
            trend={trends.completed}
            trendLabel={trendLabel}
            sparkData={completedSparkline}
            sparkColor={STATUS_COLORS.Completed}
          />
          {isAdmin ? (
            <KpiTile
              label={t("kpis.activeUsers")}
              value={kpis.activeUsers ?? 0}
              icon={<Users className="w-5 h-5" />}
              accent="purple"
              trendLabel={trendLabel}
              sparkColor={STATUS_COLORS.ReadyForPickup}
            />
          ) : (
            <KpiTile
              label={t("kpis.myOpenShipments")}
              value={kpis.myOpenShipments ?? 0}
              icon={<ListChecks className="w-5 h-5" />}
              accent="orange"
              trendLabel={trendLabel}
              sparkColor={STATUS_COLORS.AwaitingPickup}
            />
          )}
        </div>
      )}

      {/* Shipments over time — carrier-platform-style area chart */}
      {initialLoading ? (
        <Skeleton type="chart" />
      ) : (
        <ShipmentsOverTimeCard
          data={chartData}
          totalLabel={t("kpis.totalShipments")}
          completedLabel={t("statusLabels.completed")}
          inTransitLabel={t("statusLabels.inTransit")}
          awaitingPickupLabel={t("statusLabels.pickedUp")}
          readyForPickupLabel={t("statusLabels.readyForPickup")}
          orderLabel={t("statusLabels.order")}
          title={t("trends.title")}
          subtitle={t("trends.subtitle")}
          emptyLabel={t("trends.empty")}
          avgPerDayLabel={t("trends.avgPerDay")}
          totalsTrend={trends.totalShipments}
          trendLabel={trendLabel}
          periodDays={period.days || 30}
        />
      )}

      {/* Middle row — Donut + (Members for admin / Quick Actions for user) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Donut */}
        {initialLoading ? (
          <Skeleton type="chart" />
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-dark-900 flex items-center gap-2 mb-1">
              <TrendingUp className="h-5 w-5 text-primary-600" />
              {t("shipmentOverview.title")}
            </h2>
            <p className="text-xs text-dark-500 mb-3">
              {t("shipmentOverview.totalShipments")}: {totalForChart}
            </p>
            {totalForChart === 0 ? (
              <div className="h-56 flex items-center justify-center text-sm text-gray-500">
                {t("trends.empty")}
              </div>
            ) : (
              <>
                <div className="h-56 relative">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={donutData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={2}
                        dataKey="value"
                        nameKey="name"
                        activeShape={DonutActiveShape}
                        isAnimationActive
                      >
                        {donutData.map((entry) => (
                          <Cell key={entry.key} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={tooltipContentStyle}
                        labelStyle={tooltipLabelStyle}
                        itemStyle={tooltipItemStyle}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <div className="text-2xl font-bold text-dark-900">
                      {totalForChart}
                    </div>
                    <div className="text-xs text-dark-500 uppercase tracking-wide">
                      {t("statusDonut.centerLabel")}
                    </div>
                  </div>
                </div>
                <div className="mt-3 space-y-1.5">
                  {donutData.map((d) => {
                    const pct =
                      totalForChart > 0
                        ? Math.round((d.value / totalForChart) * 100)
                        : 0;
                    return (
                      <div
                        key={d.key}
                        className="flex items-center justify-between text-sm"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span
                            className="w-2.5 h-2.5 rounded-full shrink-0"
                            style={{ backgroundColor: d.color }}
                          />
                          <span className="text-dark-700 truncate">
                            {d.name}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-dark-900 font-semibold">
                          <span className="text-xs text-dark-500 font-medium">
                            {pct}%
                          </span>
                          <span>{d.value}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

        {/* Right column: members (admin) or quick actions (user) */}
        {isAdmin ? (
          initialLoading ? (
            <Skeleton type="members" />
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-dark-900 flex items-center gap-2">
                  <Users className="h-5 w-5 text-primary-600" />
                  <span>{t("newMembers.title")}</span>
                </h2>
                <Link
                  to="/users"
                  className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center gap-1 transition-colors"
                >
                  <span>{t("newMembers.viewAll")}</span>
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
              {recentMembers.length > 0 ? (
                <div className="space-y-2 flex-1">
                  {recentMembers.map((member) => {
                    const daysAgo = calculateDaysAgo(member.createdAt);
                    return (
                      <div
                        key={member._id}
                        className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg transition-colors"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-9 h-9 bg-primary-50 rounded-full flex items-center justify-center shrink-0">
                            <Users className="h-4 w-4 text-primary-600" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-dark-900 truncate">
                              {member.firstName} {member.lastName}
                            </p>
                            <p className="text-xs text-dark-500 truncate">
                              {member.email}
                            </p>
                          </div>
                        </div>
                        <div
                          className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                            daysAgo <= 7
                              ? "bg-green-50 text-green-700"
                              : "bg-blue-50 text-blue-700"
                          }`}
                        >
                          <Calendar className="h-3 w-3" />
                          <span>{daysAgo}d</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <span className="text-sm text-gray-500">
                    {t("newMembers.noNewUsers")}
                  </span>
                </div>
              )}
            </div>
          )
        ) : (
          <QuickActionsCard
            t={t}
            onNewShipment={(type) => navigate(`/shipments/new?type=${type}`)}
            onAddPartner={() => navigate("/partners?new=true")}
            onAddAddress={() => navigate("/addresses?new=true")}
          />
        )}
      </div>

      {/* Bottom row — Recent shipments + (admin's Quick Actions) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className={isAdmin ? "lg:col-span-2" : "lg:col-span-3"}>
          {initialLoading ? (
            <Skeleton type="table" />
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg font-semibold text-dark-900 flex items-center gap-2">
                  <Package className="h-5 w-5 text-primary-600" />
                  <span>{t("recentShipments.title")}</span>
                </h2>
                {canOpenShipments && (
                  <Link
                    to="/shipments"
                    className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center gap-1 transition-colors"
                  >
                    <span>{t("recentShipments.viewAll")}</span>
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                )}
              </div>
              {recentShipments.length === 0 ? (
                <div className="py-10 text-center text-sm text-gray-500">
                  {t("recentShipments.empty")}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        {(
                          [
                            "id",
                            "route",
                            "status",
                            "weight",
                            "shipmentDate",
                            "createdBy",
                          ] as const
                        ).map((col) => (
                          <th
                            key={col}
                            className="px-4 py-2.5 text-left text-xs font-medium text-dark-500 uppercase tracking-wider"
                          >
                            {t(`recentShipments.columns.${col}`)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {recentShipments.map((shipment) => (
                        <tr
                          key={shipment._id || shipment.shipmentNumber}
                          onClick={
                            canOpenShipments && shipment._id
                              ? () => navigate(`/shipments/${shipment._id}`)
                              : undefined
                          }
                          className={`hover:bg-gray-50 transition-colors ${
                            canOpenShipments ? "cursor-pointer" : ""
                          }`}
                        >
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-dark-900">
                            {shipment.shipmentNumber}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-dark-600">
                            {shipment?.pickup?.companyName || "—"} →{" "}
                            {shipment?.delivery?.companyName || "—"}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span
                              className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${getStatusBadgeColor(
                                shipment.status,
                              )}`}
                            >
                              {STATUS_STEPS.find((s) => s.key === shipment.status)
                                ?.label || shipment.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-dark-600">
                            {shipment?.totalWeight || 0}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-dark-600">
                            {shipment.shipmentDate?.split("T")[0]}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-dark-500">
                            {shipment.createdBy?.firstName}{" "}
                            {shipment.createdBy?.lastName}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {isAdmin && (
          <QuickActionsCard
            t={t}
            onNewShipment={(type) => navigate(`/shipments/new?type=${type}`)}
            onAddPartner={() => navigate("/partners?new=true")}
            onAddAddress={() => navigate("/addresses?new=true")}
          />
        )}
      </div>
    </div>
  );
};

// ── Shipments Over Time — carrier-platform-style area chart ──────────────────
type SeriesKey =
  | "completed"
  | "inTransit"
  | "awaitingPickup"
  | "readyForPickup"
  | "order";

interface ChartPoint {
  label: string;
  date: string;
  total: number;
  completed: number;
  inTransit: number;
  awaitingPickup: number;
  readyForPickup: number;
  order: number;
}

interface ShipmentsOverTimeCardProps {
  data: ChartPoint[];
  title: string;
  subtitle: string;
  emptyLabel: string;
  totalLabel: string;
  completedLabel: string;
  inTransitLabel: string;
  awaitingPickupLabel: string;
  readyForPickupLabel: string;
  orderLabel: string;
  avgPerDayLabel: string;
  totalsTrend: DashboardTrendDelta;
  trendLabel: string;
  periodDays: number;
}

interface RichTooltipPayloadItem {
  dataKey: string;
  name: string;
  value: number;
  color: string;
}

interface RichTooltipProps {
  active?: boolean;
  payload?: RichTooltipPayloadItem[];
  label?: string;
  seriesOrder: { key: SeriesKey; name: string; color: string }[];
  visible: Record<SeriesKey, boolean>;
}

const RichTooltip: React.FC<RichTooltipProps> = ({
  active,
  payload,
  label,
  seriesOrder,
  visible,
}) => {
  if (!active || !payload || payload.length === 0) return null;
  const valueFor = (key: SeriesKey) => {
    const item = payload.find((p) => p.dataKey === key);
    return item ? item.value : 0;
  };
  const total = seriesOrder
    .filter((s) => visible[s.key])
    .reduce((sum, s) => sum + valueFor(s.key), 0);
  return (
    <div className="rounded-xl bg-gray-900/95 backdrop-blur-sm text-white shadow-2xl border border-white/5 px-3 py-2.5 min-w-[180px]">
      <div className="flex items-baseline justify-between mb-2 pb-2 border-b border-white/10">
        <span className="text-[11px] uppercase tracking-wider text-gray-400">
          {label}
        </span>
        <span className="text-sm font-bold">{total}</span>
      </div>
      <div className="space-y-1">
        {seriesOrder.map((s) => {
          if (!visible[s.key]) return null;
          const v = valueFor(s.key);
          if (v === 0) return null;
          return (
            <div
              key={s.key}
              className="flex items-center justify-between gap-3 text-xs"
            >
              <div className="flex items-center gap-2">
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: s.color }}
                />
                <span className="text-gray-300">{s.name}</span>
              </div>
              <span className="font-semibold">{v}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ShipmentsOverTimeCard: React.FC<ShipmentsOverTimeCardProps> = ({
  data,
  title,
  subtitle,
  emptyLabel,
  totalLabel,
  completedLabel,
  inTransitLabel,
  awaitingPickupLabel,
  readyForPickupLabel,
  orderLabel,
  avgPerDayLabel,
  totalsTrend,
  trendLabel,
  periodDays,
}) => {
  const seriesOrder: { key: SeriesKey; name: string; color: string }[] = [
    { key: "completed", name: completedLabel, color: STATUS_COLORS.Completed },
    { key: "inTransit", name: inTransitLabel, color: STATUS_COLORS.InTransit },
    {
      key: "awaitingPickup",
      name: awaitingPickupLabel,
      color: STATUS_COLORS.AwaitingPickup,
    },
    {
      key: "readyForPickup",
      name: readyForPickupLabel,
      color: STATUS_COLORS.ReadyForPickup,
    },
    { key: "order", name: orderLabel, color: STATUS_COLORS.Order },
  ];

  const [visible, setVisible] = useState<Record<SeriesKey, boolean>>({
    completed: true,
    inTransit: true,
    awaitingPickup: true,
    readyForPickup: true,
    order: true,
  });

  const toggle = (k: SeriesKey) =>
    setVisible((prev) => {
      const next = { ...prev, [k]: !prev[k] };
      // Don't allow turning everything off
      if (!Object.values(next).some(Boolean)) return prev;
      return next;
    });

  // Header totals
  const totals = useMemo(() => {
    const init = {
      total: 0,
      completed: 0,
      inTransit: 0,
      awaitingPickup: 0,
      readyForPickup: 0,
      order: 0,
    };
    return data.reduce((acc, d) => {
      acc.total += d.total;
      acc.completed += d.completed;
      acc.inTransit += d.inTransit;
      acc.awaitingPickup += d.awaitingPickup;
      acc.readyForPickup += d.readyForPickup;
      acc.order += d.order;
      return acc;
    }, init);
  }, [data]);

  const avgPerDay =
    data.length > 0 ? Math.round((totals.total / data.length) * 10) / 10 : 0;

  const trendUp = totalsTrend && totalsTrend.value >= 0;
  const showTrend =
    totalsTrend && (totalsTrend.value !== 0 || totalsTrend.percent !== 0);

  // Smart x-axis: with long ranges, recharts' minTickGap already spaces ticks.
  // We also tighten the gap based on period length.
  const minTickGap = periodDays <= 14 ? 20 : periodDays <= 60 ? 40 : 60;

  if (data.length < 2) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-start justify-between mb-1">
          <div>
            <h2 className="text-lg font-semibold text-dark-900 flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary-600" />
              {title}
            </h2>
            <p className="text-xs text-dark-500 mt-0.5">{subtitle}</p>
          </div>
        </div>
        <div className="h-72 flex items-center justify-center text-sm text-gray-500">
          {emptyLabel}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-dark-900 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary-600" />
            {title}
          </h2>
          <p className="text-xs text-dark-500 mt-0.5">{subtitle}</p>
        </div>

        {/* Clickable legend chips */}
        <div className="flex flex-wrap items-center gap-1.5">
          {seriesOrder.map((s) => {
            const on = visible[s.key];
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => toggle(s.key)}
                aria-pressed={on}
                className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full transition-all border ${
                  on
                    ? "bg-white text-dark-700 border-gray-200 hover:bg-gray-50"
                    : "bg-gray-50 text-gray-400 border-gray-100 hover:bg-gray-100"
                }`}
              >
                <span
                  className="w-2 h-2 rounded-full transition-opacity"
                  style={{
                    backgroundColor: s.color,
                    opacity: on ? 1 : 0.35,
                  }}
                />
                {s.name}
              </button>
            );
          })}
        </div>
      </div>

      {/* Stat strip */}
      <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-px bg-gray-100 rounded-lg overflow-hidden">
        <StatStripItem
          label={totalLabel}
          value={totals.total}
          extra={
            showTrend ? (
              <span
                className={`inline-flex items-center gap-0.5 text-[11px] font-semibold ${
                  trendUp ? "text-green-600" : "text-red-600"
                }`}
                title={trendLabel}
              >
                {trendUp ? (
                  <ArrowUpRight className="w-3 h-3" />
                ) : (
                  <ArrowDownRight className="w-3 h-3" />
                )}
                {Math.abs(totalsTrend!.percent)}%
              </span>
            ) : null
          }
        />
        <StatStripItem
          label={completedLabel}
          value={totals.completed}
          dotColor={STATUS_COLORS.Completed}
        />
        <StatStripItem
          label={inTransitLabel}
          value={totals.inTransit}
          dotColor={STATUS_COLORS.InTransit}
        />
        <StatStripItem label={avgPerDayLabel} value={avgPerDay} />
      </div>

      {/* Chart — stacked bars (industry-standard for sparse / long-range data) */}
      <div className="h-72 mt-5">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{ top: 8, right: 12, left: -16, bottom: 0 }}
            barCategoryGap="20%"
          >
            <CartesianGrid
              vertical={false}
              stroke="#f1f5f9"
              strokeDasharray="2 4"
            />
            <XAxis
              dataKey="label"
              tick={{ fill: "#94a3b8", fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              tickMargin={10}
              interval="preserveStartEnd"
              minTickGap={minTickGap}
            />
            <YAxis
              tick={{ fill: "#94a3b8", fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              tickMargin={8}
              allowDecimals={false}
              width={40}
            />
            <Tooltip
              cursor={{ fill: "rgba(37, 99, 235, 0.04)" }}
              content={
                <RichTooltip seriesOrder={seriesOrder} visible={visible} />
              }
            />
            {seriesOrder.map((s, idx) => {
              if (!visible[s.key]) return null;
              const isLast =
                seriesOrder.findLast?.((x) => visible[x.key])?.key === s.key ||
                seriesOrder
                  .slice()
                  .reverse()
                  .find((x) => visible[x.key])?.key === s.key;
              return (
                <Bar
                  key={s.key}
                  dataKey={s.key}
                  stackId="1"
                  fill={s.color}
                  name={s.name}
                  // Round only the top of the topmost visible stack
                  radius={isLast ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                  isAnimationActive
                  animationDuration={400}
                  animationBegin={idx * 50}
                />
              );
            })}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const StatStripItem: React.FC<{
  label: string;
  value: number;
  dotColor?: string;
  extra?: React.ReactNode;
}> = ({ label, value, dotColor, extra }) => (
  <div className="bg-white px-4 py-3">
    <div className="flex items-center gap-1.5 mb-1">
      {dotColor && (
        <span
          className="w-2 h-2 rounded-full shrink-0"
          style={{ backgroundColor: dotColor }}
        />
      )}
      <span className="text-[11px] uppercase tracking-wider text-dark-500 font-medium">
        {label}
      </span>
    </div>
    <div className="flex items-baseline gap-2">
      <span className="text-xl font-bold text-dark-900">{value}</span>
      {extra}
    </div>
  </div>
);

// ── Quick Actions card ───────────────────────────────────────────────────────
interface QuickActionsCardProps {
  t: (key: string) => string;
  onNewShipment: (type: "International" | "Domestic") => void;
  onAddPartner: () => void;
  onAddAddress: () => void;
}

/**
 * Every action here is a shortcut into another module, so each one is shown
 * only when the tenant actually has that module. The card disappears entirely
 * rather than standing empty when none of them survive.
 */
const QuickActionsCard: React.FC<QuickActionsCardProps> = ({
  t,
  onNewShipment,
  onAddPartner,
  onAddAddress,
}) => {
  const { has } = useModules();

  const actions = [
    has("shipments") && (
      <QuickActionButton
        key="shipment-international"
        icon={<Globe className="h-4 w-4 text-blue-600" />}
        iconBg="bg-blue-50"
        label={t("quickActions.newShipmentInternational")}
        onClick={() => onNewShipment("International")}
      />
    ),
    has("shipments") && (
      <QuickActionButton
        key="shipment-domestic"
        icon={<Truck className="h-4 w-4 text-orange-600" />}
        iconBg="bg-orange-50"
        label={t("quickActions.newShipmentDomestic")}
        onClick={() => onNewShipment("Domestic")}
      />
    ),
    has("partners") && (
      <QuickActionButton
        key="partner"
        icon={<Building2 className="h-4 w-4 text-purple-600" />}
        iconBg="bg-purple-50"
        label={t("quickActions.newPartner")}
        onClick={onAddPartner}
      />
    ),
    has("addresses") && (
      <QuickActionButton
        key="address"
        icon={<MapPin className="h-4 w-4 text-green-600" />}
        iconBg="bg-green-50"
        label={t("quickActions.newAddress")}
        onClick={onAddAddress}
      />
    ),
  ].filter(Boolean);

  if (actions.length === 0) return null;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h2 className="text-lg font-semibold text-dark-900 flex items-center gap-2 mb-4">
        <Plus className="h-5 w-5 text-primary-600" />
        <span>{t("quickActions.title")}</span>
      </h2>
      <div className="space-y-2">{actions}</div>
    </div>
  );
};

interface QuickActionButtonProps {
  icon: React.ReactNode;
  iconBg: string;
  label: string;
  onClick: () => void;
}

const QuickActionButton: React.FC<QuickActionButtonProps> = ({
  icon,
  iconBg,
  label,
  onClick,
}) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50/40 transition-colors text-left group"
  >
    <div
      className={`w-9 h-9 rounded-lg flex items-center justify-center ${iconBg}`}
    >
      {icon}
    </div>
    <span className="text-sm font-medium text-dark-900 flex-1">{label}</span>
    <ArrowRight className="h-4 w-4 text-gray-400 group-hover:text-primary-600 transition-colors" />
  </button>
);

export default DashboardPage;
